// src/pages/Courses/LessonPlayer.tsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ICourse } from "./CoursesPage";
import "./LessonPlayer.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001/api/v1";
const AUTO_ADVANCE_SECS = 5;

/* ════════════════════════════════════════════
   PROGRESS — localStorage + backend
════════════════════════════════════════════ */
const STORAGE_KEY = (slug: string) => `acd_progress_${slug}`;

function loadProgress(slug: string): Set<number> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(slug));
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as number[]);
  } catch { return new Set(); }
}

function saveProgress(slug: string, completed: Set<number>) {
  try {
    localStorage.setItem(STORAGE_KEY(slug), JSON.stringify(Array.from(completed)));
  } catch {}
}

async function syncProgressToBackend(slug: string, globalIdx: number) {
  try {
    const token = localStorage.getItem("token");
    if (!token) return;
    await fetch(`${API_BASE}/courses/${slug}/progress`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body:    JSON.stringify({ lessonIdx: globalIdx, completed: true }),
    });
  } catch {}
}

/* ════════════════════════════════════════════
   TYPES
════════════════════════════════════════════ */
interface FlatLesson {
  globalIdx:    number;
  lesson_id:    string;
  title:        string;
  subtitle:     string;
  video_url:    string;
  duration_sec: number;
  is_preview:   boolean;
  moduleTitle:  string;
  moduleIdx:    number;   // which module this lesson belongs to
  durationFmt:  string;
  poster:  string;
}

interface ModuleGroup {
  moduleIdx:   number;
  moduleTitle: string;
  lessons:     FlatLesson[];
}

/* ════════════════════════════════════════════
   ICONS
════════════════════════════════════════════ */
const IcoArrow       = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>);
const IcoCheck       = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>);
const IcoThumb       = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"/><path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>);
const IcoShare       = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>);
const IcoComment     = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>);
const IcoNote        = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>);
const IcoList        = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>);
const IcoPlay        = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="5 3 19 12 5 21 5 3"/></svg>);
const IcoPause       = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" stroke="none"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>);
const IcoRewind      = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.96"/></svg>);
const IcoFastFwd     = () => (<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.49-3.96"/></svg>);
const IcoVolume      = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>);
const IcoVolumeMid   = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>);
const IcoVolumeLow   = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/></svg>);
const IcoMute        = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>);
const IcoFullscreen  = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>);
const IcoExitFS      = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/><line x1="10" y1="14" x2="3" y2="21"/><line x1="21" y1="3" x2="14" y2="10"/></svg>);
const IcoSpeed       = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>);
const IcoPlaySmall   = () => (<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="none"><polygon points="5 3 19 12 5 21 5 3"/></svg>);
const IcoCheckDone   = () => (<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>);
const IcoDownload    = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>);
const IcoBook        = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>);
const IcoLink        = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>);
const IcoFlashRewind = () => (<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.96"/></svg>);
const IcoFlashFwd    = () => (<svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.49-3.96"/></svg>);
const IcoNext        = () => (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 4 15 12 5 20 5 4"/><line x1="19" y1="5" x2="19" y2="19"/></svg>);
/* FIX 1: Chevron for accordion */
const IcoChevron     = ({ open }: { open: boolean }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.22s" }}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

const INIT_COMMENTS = [
  { id:1, name:"Sarah Johnson", initial:"S", color:"#8b5cf6", time:"2 days ago",  text:"This lesson was incredibly helpful! The examples were clear and easy to follow.", likes:24 },
  { id:2, name:"Mike Chen",     initial:"M", color:"#ec4899", time:"1 day ago",   text:"Can someone help me with the exercise at 12:30? I am stuck on that part.", likes:8 },
  { id:3, name:"Priya Nair",    initial:"P", color:"#f59e0b", time:"5 hours ago", text:"Amazing explanation! I finally understand recursion after watching this twice.", likes:17 },
];

const fmt = (s: number) => { const m = Math.floor(s/60), sec = Math.floor(s%60); return `${m}:${sec<10?"0":""}${sec}`; };

/* Volume icon based on level */
const VolumeIcon = ({ volume, muted }: { volume: number; muted: boolean }) => {
  if (muted || volume === 0) return <IcoMute />;
  if (volume < 0.4)          return <IcoVolumeLow />;
  if (volume < 0.75)         return <IcoVolumeMid />;
  return <IcoVolume />;
};

/* ════════════════════════════════════════════
   COMPONENT
════════════════════════════════════════════ */
const LessonPlayer: React.FC = () => {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId?: string }>();
  const navigate = useNavigate();

  /* ── Course + lessons state ── */
  const [course,        setCourse]        = useState<ICourse | null>(null);
  const [allLessons,    setAllLessons]    = useState<FlatLesson[]>([]);
  const [moduleGroups,  setModuleGroups]  = useState<ModuleGroup[]>([]);
  const [courseLoading, setCourseLoading] = useState(true);
  /* FIX 1: which module accordions are open in the sidebar */
  const [openModules,   setOpenModules]   = useState<Set<number>>(new Set([0]));

  const lessonIdx = Math.min(Number(lessonId ?? 0), Math.max(0, allLessons.length - 1));
  const lesson    = allLessons[lessonIdx];
  const hasNext   = lessonIdx < allLessons.length - 1;

  /* ── Video refs ── */
  const videoRef     = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  /* ── Video state ── */
  const [playing,      setPlaying]      = useState(false);
  const [currentTime,  setCurrentTime]  = useState(0);
  const [duration,     setDuration]     = useState(0);
  const [volume,       setVolume]       = useState(1);
  const [muted,        setMuted]        = useState(false);
  const [speed,        setSpeed]        = useState(1);
  const [showSpeed,    setShowSpeed]    = useState(false);
  /* FIX 2: volume slider stays open on hover/focus — use a proper ref-based approach */
  const [showVolBar,   setShowVolBar]   = useState(false);
  const volWrapRef = useRef<HTMLDivElement>(null);
  const [buffered,     setBuffered]     = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [skipFlash,    setSkipFlash]    = useState<"none"|"left"|"right">("none");
  const skipFlashTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ── Progress ── */
  const [completedSet, setCompletedSet] = useState<Set<number>>(() =>
    courseId ? loadProgress(courseId) : new Set()
  );
  const isCompleted = completedSet.has(lessonIdx);

  /* ── Auto-advance ── */
  const [showAutoAdvance,  setShowAutoAdvance]  = useState(false);
  const [autoAdvanceCount, setAutoAdvanceCount] = useState(AUTO_ADVANCE_SECS);
  const autoAdvanceTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  /* ── Sidebar ── */
  const [activeTab,   setActiveTab]   = useState<"content"|"comments"|"notes">("content");
  const [commentText, setCommentText] = useState("");
  const [comments,    setComments]    = useState(INIT_COMMENTS);
  const [likedIds,    setLikedIds]    = useState<Set<number>>(new Set());
  const [noteText,    setNoteText]    = useState("");
  const [notes,       setNotes]       = useState<string[]>([]);

  /* ── Fetch course + flatten lessons ── */
  useEffect(() => {
    if (!courseId) return;
    setCourseLoading(true);
    fetch(`${API_BASE}/courses/${courseId}`)
      .then(r => r.json())
      .then(d => {
        if (!d.success) return;
        const c: ICourse = d.data;
        setCourse(c);

        let idx = 0;
        const flat: FlatLesson[] = [];
        const groups: ModuleGroup[] = [];

        c.modules.forEach((mod, mi) => {
          const sorted = [...mod.lessons].sort((a, b) => a.order - b.order);
          const group: ModuleGroup = { moduleIdx: mi, moduleTitle: mod.title, lessons: [] };
          sorted.forEach(l => {
            const fl: FlatLesson = {
              globalIdx:    idx++,
              lesson_id:    l.lesson_id,
              title:        l.title,
              subtitle:     l.subtitle,
              video_url:    l.video_url,
              poster:       l.poster,
              duration_sec: l.duration_sec,
              is_preview:   l.is_preview,
              moduleTitle:  mod.title,
              moduleIdx:    mi,
              durationFmt:  `${Math.floor(l.duration_sec/60)}:${String(l.duration_sec%60).padStart(2,"0")}`,
            };
            flat.push(fl);
            group.lessons.push(fl);
          });
          groups.push(group);
        });

        setAllLessons(flat);
        setModuleGroups(groups);
        setCompletedSet(loadProgress(courseId));

        // FIX 1: auto-open the module that contains the current lesson
        const currentLesson = flat[Math.min(Number(lessonId ?? 0), flat.length - 1)];
        if (currentLesson) setOpenModules(new Set([currentLesson.moduleIdx]));
      })
      .finally(() => setCourseLoading(false));
  }, [courseId, lessonId]);

  /* Auto-open correct module when lesson changes */
  useEffect(() => {
    if (lesson) {
      setOpenModules(prev => {
        const next = new Set(prev);
        next.add(lesson.moduleIdx);
        return next;
      });
    }
  }, [lesson]);

  /* Reset player on lesson change */
  useEffect(() => {
    setPlaying(false); setCurrentTime(0); setDuration(0); setBuffered(0);
    setShowAutoAdvance(false); setAutoAdvanceCount(AUTO_ADVANCE_SECS);
    if (autoAdvanceTimer.current) clearInterval(autoAdvanceTimer.current);
  }, [lessonIdx]);

  /* Re-load progress */
  useEffect(() => {
    if (courseId) setCompletedSet(loadProgress(courseId));
  }, [courseId]);

  /* ── Video event listeners ── */
  const onTimeUpdate = useCallback(() => {
    const v = videoRef.current; if (!v) return;
    setCurrentTime(v.currentTime);
    if (v.buffered.length > 0) setBuffered(v.buffered.end(v.buffered.length - 1));
  }, []);
  const onLoaded = useCallback(() => {
    const v = videoRef.current; if (!v) return;
    setDuration(v.duration); setPlaying(false);
  }, []);
  const onEnded = useCallback(() => {
    setPlaying(false);
    if (!courseId) return;
    setCompletedSet(prev => {
      const next = new Set(prev);
      next.add(lessonIdx);
      saveProgress(courseId, next);
      syncProgressToBackend(courseId, lessonIdx);
      return next;
    });
    if (lessonIdx < allLessons.length - 1) {
      setShowAutoAdvance(true);
      setAutoAdvanceCount(AUTO_ADVANCE_SECS);
    }
  }, [courseId, lessonIdx, allLessons.length]);

  useEffect(() => {
    const v = videoRef.current; if (!v) return;
    v.addEventListener("timeupdate", onTimeUpdate);
    v.addEventListener("loadedmetadata", onLoaded);
    v.addEventListener("ended", onEnded);
    return () => {
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("loadedmetadata", onLoaded);
      v.removeEventListener("ended", onEnded);
    };
  }, [onTimeUpdate, onLoaded, onEnded, lessonIdx]);

  /* Fullscreen change */
  useEffect(() => {
    const fn = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", fn);
    document.addEventListener("webkitfullscreenchange", fn);
    return () => {
      document.removeEventListener("fullscreenchange", fn);
      document.removeEventListener("webkitfullscreenchange", fn);
    };
  }, []);

  /* FIX 2: Close volume when clicking outside the vol-wrap */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (volWrapRef.current && !volWrapRef.current.contains(e.target as Node)) {
        setShowVolBar(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* Keyboard shortcuts */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (e.code === "Space")      { e.preventDefault(); togglePlay(); }
      if (e.code === "ArrowRight") skip(10);
      if (e.code === "ArrowLeft")  skip(-10);
      if (e.code === "KeyM")       toggleMute();
      if (e.code === "KeyF")       toggleFullscreen();
      if (e.code === "Escape" && showAutoAdvance) cancelAutoAdvance();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  /* Auto-advance countdown */
  useEffect(() => {
    if (!showAutoAdvance || !courseId) return;
    autoAdvanceTimer.current = setInterval(() => {
      setAutoAdvanceCount(p => {
        if (p <= 1) {
          clearInterval(autoAdvanceTimer.current!);
          navigate(`/courses/${courseId}/learn/${lessonIdx + 1}`);
          return 0;
        }
        return p - 1;
      });
    }, 1000);
    return () => { if (autoAdvanceTimer.current) clearInterval(autoAdvanceTimer.current); };
  }, [showAutoAdvance, courseId, lessonIdx, navigate]);

  /* ── Controls ── */
  const togglePlay = () => {
    const v = videoRef.current; if (!v) return;
    if (showAutoAdvance) { cancelAutoAdvance(); return; }
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };

  const flashSkip = (dir: "left"|"right") => {
    setSkipFlash(dir);
    if (skipFlashTimer.current) clearTimeout(skipFlashTimer.current);
    skipFlashTimer.current = setTimeout(() => setSkipFlash("none"), 600);
  };

  const skip = (sec: number) => {
    const v = videoRef.current; if (!v) return;
    v.currentTime = Math.max(0, Math.min(v.duration||0, v.currentTime + sec));
    flashSkip(sec > 0 ? "right" : "left");
  };

  const seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = videoRef.current; if (!v) return;
    v.currentTime = Number(e.target.value); setCurrentTime(Number(e.target.value));
  };

  /* FIX 2: Volume slider properly updates video volume */
  const changeVolume = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = videoRef.current; if (!v) return;
    const val = Number(e.target.value);
    v.volume = val;
    setVolume(val);
    if (val === 0) { v.muted = true; setMuted(true); }
    else           { v.muted = false; setMuted(false); }
  };

  const toggleMute = () => {
    const v = videoRef.current; if (!v) return;
    if (muted || volume === 0) {
      v.muted = false; setMuted(false);
      // restore to a sensible volume if it was 0
      if (volume === 0) { v.volume = 0.7; setVolume(0.7); }
    } else {
      v.muted = true; setMuted(true);
    }
  };

  const changeSpeed = (s: number) => {
    const v = videoRef.current; if (!v) return;
    v.playbackRate = s; setSpeed(s); setShowSpeed(false);
  };

  const toggleFullscreen = async () => {
    const el = containerRef.current; if (!el) return;
    if (!document.fullscreenElement) {
      try {
        await el.requestFullscreen();
        const orientation = (window.screen as any).orientation;
        if (orientation?.lock) { try { await orientation.lock("landscape"); } catch {} }
      } catch {}
    } else {
      await document.exitFullscreen();
      const orientation = (window.screen as any).orientation;
      if (orientation?.unlock) { try { orientation.unlock(); } catch {} }
    }
  };

  const toggleComplete = () => {
    if (!courseId) return;
    setCompletedSet(prev => {
      const next = new Set(prev);
      next.has(lessonIdx) ? next.delete(lessonIdx) : next.add(lessonIdx);
      saveProgress(courseId, next); return next;
    });
  };

  const cancelAutoAdvance = () => {
    setShowAutoAdvance(false);
    if (autoAdvanceTimer.current) clearInterval(autoAdvanceTimer.current);
    setAutoAdvanceCount(AUTO_ADVANCE_SECS);
  };

  /* FIX 1: Toggle module open/closed */
  const toggleModule = (mi: number) => {
    setOpenModules(prev => {
      const next = new Set(prev);
      next.has(mi) ? next.delete(mi) : next.add(mi);
      return next;
    });
  };

  const submitComment = () => {
    if (!commentText.trim()) return;
    setComments(p => [{ id:Date.now(), name:"You", initial:"Y", color:"#0d9e8a", time:"just now", text:commentText.trim(), likes:0 }, ...p]);
    setCommentText("");
  };
  const toggleLike = (id: number) => setLikedIds(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const saveNote   = () => { if (!noteText.trim()) return; setNotes(p => [...p, noteText.trim()]); setNoteText(""); };

  const pct          = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufPct       = duration > 0 ? (buffered    / duration) * 100 : 0;
  const completedCount = completedSet.size;
  const progressPct    = allLessons.length > 0 ? Math.round((completedCount / allLessons.length) * 100) : 0;
  const nextLesson     = hasNext ? allLessons[lessonIdx + 1] : null;

  /* Loading state */
  if (courseLoading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100vh", background:"#0f1117", color:"#e8edf2", flexDirection:"column", gap:16 }}>
      <div style={{ width:44, height:44, borderRadius:"50%", border:"4px solid rgba(13,158,138,0.2)", borderTopColor:"#0d9e8a", animation:"spin 0.8s linear infinite" }} />
      <p style={{ color:"#6b7a8d", fontWeight:600, fontSize:"0.9rem" }}>Loading course…</p>
      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );

  if (!course || !lesson) return (
    <div style={{ textAlign:"center", padding:80, color:"#fff", background:"#0f1117" }}>
      <h2>Course or lesson not found</h2>
      <Link to="/courses" style={{ color:"#0d9e8a" }}>← Back to Courses</Link>
    </div>
  );

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <>
      <Helmet><title>{lesson.title} – {course.title} – Acadebility</title></Helmet>

      <div className="lp-page">

        {/* TOP BAR */}
        <div className="lp-topbar">
          <Link to={`/courses/${course.slug}`} className="lp-back-btn">
            <IcoArrow /> Back to Course
          </Link>
          <div className="lp-topbar-center">
            <div className="lp-topbar-course">{course.title}</div>
            <div className="lp-topbar-lesson">{lesson.title}</div>
          </div>
          <button className={`lp-complete-btn${isCompleted ? " completed" : ""}`} onClick={toggleComplete}>
            <IcoCheck />
            {isCompleted ? "Completed" : "Mark Complete"}
          </button>
        </div>

        {/* BODY */}
        <div className="lp-body">
          <div className="lp-left">

            {/* ══════════ VIDEO PLAYER ══════════ */}
            <div className="lp-video-wrap" ref={containerRef}>
              <video
                ref={videoRef}
                className="lp-video-el"
                poster={lesson.poster}
                preload="metadata"
                src={lesson.video_url}
                onError={e => console.error("VIDEO ERROR:", e)}
              >
                <source src={lesson.video_url} type="video/mp4" />
              </video>

              {/* Centre play button */}
              {!playing && !showAutoAdvance && (
                <div className="lp-video-overlay">
                  <button className="lp-play-center" aria-label="Play" onClick={togglePlay}>
                    <svg width="64" height="64" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="11" fill="rgba(255,255,255,0.93)"/>
                      <polygon points="10 7.5 17 12 10 16.5 10 7.5" fill="#0d9e8a"/>
                    </svg>
                  </button>
                </div>
              )}

              {/* Auto-advance overlay */}
              {showAutoAdvance && nextLesson && (
                <div className="lp-autoadvance-overlay">
                  <div className="lp-aa-done-badge">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Lesson completed!
                  </div>
                  <div className="lp-aa-card">
                    <div className="lp-aa-label">Up Next</div>
                    <div className="lp-aa-title">{nextLesson.title}</div>
                    <div className="lp-aa-sub">{nextLesson.subtitle}</div>
                    <div className="lp-aa-countdown-wrap">
                      <svg className="lp-aa-ring" viewBox="0 0 56 56">
                        <circle className="lp-aa-ring-bg" cx="28" cy="28" r="24"/>
                        <circle className="lp-aa-ring-fill" cx="28" cy="28" r="24"
                          style={{ strokeDashoffset: 150.8 * (autoAdvanceCount / AUTO_ADVANCE_SECS) }} />
                      </svg>
                      <span className="lp-aa-countdown-num">{autoAdvanceCount}</span>
                    </div>
                    <div className="lp-aa-actions">
                      <button className="lp-aa-btn-next" onClick={() => navigate(`/courses/${courseId}/learn/${lessonIdx + 1}`)}>
                        <IcoNext /> Play Now
                      </button>
                      <button className="lp-aa-btn-cancel" onClick={cancelAutoAdvance}>Cancel</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Skip flash */}
              <div className={`lp-skip-flash${skipFlash !== "none" ? ` show-${skipFlash}` : ""}`}>
                <div className="lp-skip-flash-left"><div className="lp-skip-flash-icon"><IcoFlashRewind /><span>-10s</span></div></div>
                <div className="lp-skip-flash-right"><div className="lp-skip-flash-icon"><IcoFlashFwd /><span>+10s</span></div></div>
              </div>

              {/* ── Controls bar ── */}
              <div className="lp-controls">
                {/* FIX 4: Progress bar BELOW the button row */}
                <div className="lp-progress-wrap">
                  <div className="lp-progress-bg" />
                  <div className="lp-progress-buffered" style={{ width:`${bufPct}%` }} />
                  <div className="lp-progress-played"   style={{ width:`${pct}%` }} />
                  <input
                    type="range" className="lp-progress-input"
                    min={0} max={duration||100} step={0.1}
                    value={currentTime} onChange={seek}
                  />
                </div>
                {/* FIX 4: progress bar moved to bottom of controls, after the button row */}
                <div className="lp-ctrl-row">
                  {/* Left cluster */}
                  <div className="lp-ctrl-left">
                    <button className="lp-ctrl-btn" onClick={togglePlay} title={playing ? "Pause (Space)" : "Play (Space)"}>
                      {playing ? <IcoPause /> : <IcoPlay />}
                    </button>
                    <button className="lp-ctrl-btn lp-skip-btn" onClick={() => skip(-10)} title="Rewind 10s">
                      <IcoRewind /><span className="lp-skip-label">10s</span>
                    </button>
                    <button className="lp-ctrl-btn lp-skip-btn" onClick={() => skip(10)} title="Forward 10s">
                      <IcoFastFwd /><span className="lp-skip-label">10s</span>
                    </button>

                    {/* FIX 2: Volume — horizontal slider, stays visible on hover/click */}
                    <div
                      className={`lp-vol-wrap${showVolBar ? " vol-open" : ""}`}
                      ref={volWrapRef}
                    >
                      <button
                        className="lp-ctrl-btn lp-vol-icon-btn"
                        onClick={() => setShowVolBar(p => !p)}
                        title="Volume"
                      >
                        <VolumeIcon volume={volume} muted={muted} />
                      </button>
                      {/* Horizontal slider — always rendered when open, no disappear on mouse leave */}
                      <div className="lp-vol-slider-wrap">
                        <input
                          type="range"
                          className="lp-vol-slider"
                          min={0} max={1} step={0.02}
                          value={muted ? 0 : volume}
                          onChange={changeVolume}
                          title={`Volume: ${Math.round((muted ? 0 : volume) * 100)}%`}
                        />
                        <span className="lp-vol-pct">{Math.round((muted ? 0 : volume) * 100)}%</span>
                      </div>
                    </div>

                    <span className="lp-time">{fmt(currentTime)} / {fmt(duration||0)}</span>
                  </div>

                  {/* Right cluster */}
                  <div className="lp-ctrl-right">
                    <div className="lp-speed-wrap">
                      <button className="lp-ctrl-btn lp-ctrl-text" onClick={() => setShowSpeed(p => !p)} title="Playback speed">
                        <IcoSpeed /> {speed}x
                      </button>
                      {showSpeed && (
                        <div className="lp-speed-menu">
                          {[0.5,0.75,1,1.25,1.5,1.75,2].map(s => (
                            <button key={s} className={`lp-speed-item${speed===s?" active":""}`} onClick={() => changeSpeed(s)}>{s}x</button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button className="lp-ctrl-btn" onClick={toggleFullscreen} title="Fullscreen (F)">
                      {isFullscreen ? <IcoExitFS /> : <IcoFullscreen />}
                    </button>
                  </div>
                </div>

                
              </div>
            </div>

            {/* ══════════ LESSON INFO — FIX 5: left-aligned ══════════ */}
            <div className="lp-info-card">
              <div className="lp-info-top">
                <div>
                  <h1 className="lp-lesson-title">{lesson.title}</h1>
                  <p  className="lp-lesson-sub">{lesson.subtitle}</p>
                </div>
                {isCompleted && (
                  <div className="lp-completed-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    Completed
                  </div>
                )}
              </div>
              <div className="lp-info-divider" />
              <div className="lp-actions">
                <button className="lp-action-btn"><IcoThumb />   Like</button>
                <button className="lp-action-btn"><IcoShare />   Share</button>
                <button className="lp-action-btn"><IcoDownload />Resources</button>
                <button className="lp-action-btn"><IcoBook />    Transcript</button>
              </div>
            </div>

            {/* About — FIX 5: left-aligned */}
            <div className="lp-extra-card">
              <h2 className="lp-extra-title">📖 About this Lesson</h2>
              <p  className="lp-extra-body">{lesson.subtitle}</p>
              <div className="lp-tags-row">
                <span className="lp-tag">{course.level}</span>
                <span className="lp-tag">{course.category}</span>
                {course.is_live     && <span className="lp-tag">🔴 Live</span>}
                {course.is_recorded && <span className="lp-tag">⏺ Recorded</span>}
              </div>
            </div>

            {/* Instructor — FIX 5: left-aligned */}
            <div className="lp-extra-card">
              <h2 className="lp-extra-title">👩‍🏫 Your Instructor</h2>
              <div className="lp-teacher-row">
                <div className="lp-teacher-avatar">{course.instructor_avatar}</div>
                <div className="lp-teacher-info">
                  <div className="lp-teacher-name">{course.instructor}</div>
                  <div className="lp-teacher-role">{course.instructor_title}</div>
                  <div className="lp-teacher-stats">
                    <span>⭐ {course.rating} rating</span>
                    <span>👥 {course.students_display} Students</span>
                    <span>🎓 {course.modules.length} Modules</span>
                  </div>
                  <p className="lp-teacher-bio">{course.instructor_bio}</p>
                </div>
              </div>
            </div>

            {/* Resources — FIX 5: left-aligned */}
            <div className="lp-extra-card">
              <h2 className="lp-extra-title">📎 Lesson Resources</h2>
              <div className="lp-resources-list">
                {[
                  { icon:<IcoDownload />, name:"Lesson Slides (PDF)", size:"1.2 MB" },
                  { icon:<IcoBook />,     name:"Code Starter Files",  size:"34 KB"  },
                  { icon:<IcoLink />,     name:"Recommended Reading", size:"Link"   },
                ].map(r => (
                  <button className="lp-resource-item" key={r.name}>
                    <span className="lp-resource-icon">{r.icon}</span>
                    <span className="lp-resource-name">{r.name}</span>
                    <span className="lp-resource-size">{r.size}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="lp-nav-row">
              {lessonIdx > 0 && (
                <button className="lp-nav-btn" onClick={() => navigate(`/courses/${courseId}/learn/${lessonIdx-1}`)}>← Previous</button>
              )}
              {hasNext && (
                <button className="lp-nav-btn lp-nav-next" onClick={() => navigate(`/courses/${courseId}/learn/${lessonIdx+1}`)}>Next Lesson →</button>
              )}
            </div>
          </div>

          {/* ══════════ RIGHT PANEL ══════════ */}
          <div className="lp-right">
            <div className="lp-progress-strip">
              <div className="lp-progress-strip-row">
                <span>{completedCount}/{allLessons.length} lessons</span>
                <span className="lp-progress-strip-pct">{progressPct}%</span>
              </div>
              <div className="lp-progress-strip-bar">
                <div className="lp-progress-strip-fill" style={{ width:`${progressPct}%` }} />
              </div>
            </div>

            <div className="lp-tabs">
              <button className={`lp-tab${activeTab==="content"  ?" active":""}`} onClick={() => setActiveTab("content")}><IcoList /> Content</button>
              <button className={`lp-tab${activeTab==="comments" ?" active":""}`} onClick={() => setActiveTab("comments")}><IcoComment /> Comments</button>
              <button className={`lp-tab${activeTab==="notes"    ?" active":""}`} onClick={() => setActiveTab("notes")}><IcoNote /> Notes</button>
            </div>

            {/* ── FIX 1: Content tab — Module accordion with sub-topic lessons ── */}
            {activeTab === "content" && (
              <div className="lp-tab-body">
                <div className="lp-content-header">
                  <span>{allLessons.length} Lessons · {moduleGroups.length} Modules</span>
                  <span className="lp-content-header-done">{completedCount} done</span>
                </div>

                {moduleGroups.map(group => {
                  const isOpen   = openModules.has(group.moduleIdx);
                  const modDone  = group.lessons.filter(l => completedSet.has(l.globalIdx)).length;
                  const isActive = group.lessons.some(l => l.globalIdx === lessonIdx);
                  return (
                    <div className={`lp-module-group${isActive ? " lp-module-group-active" : ""}`} key={group.moduleIdx}>
                      {/* Module header — accordion toggle */}
                      <button
                        className={`lp-module-header${isOpen ? " open" : ""}`}
                        onClick={() => toggleModule(group.moduleIdx)}
                      >
                        <div className="lp-module-num">{group.moduleIdx + 1}</div>
                        <div className="lp-module-info">
                          <div className="lp-module-title">{group.moduleTitle}</div>
                          <div className="lp-module-meta">{group.lessons.length} lessons · {modDone} completed</div>
                        </div>
                        <IcoChevron open={isOpen} />
                      </button>

                      {/* Lesson list — only visible when accordion is open */}
                      {isOpen && (
                        <div className="lp-module-lessons">
                          {group.lessons.map(l => (
                            <button
                              key={l.globalIdx}
                              className={`lp-content-item${l.globalIdx===lessonIdx?" active":""}${completedSet.has(l.globalIdx)?" done":""}`}
                              onClick={() => navigate(`/courses/${courseId}/learn/${l.globalIdx}`)}
                            >
                              <div className="lp-content-icon">
                                {completedSet.has(l.globalIdx)
                                  ? <IcoCheckDone />
                                  : l.globalIdx===lessonIdx ? <IcoPlay /> : <IcoPlaySmall />
                                }
                              </div>
                              <div className="lp-content-text">
                                <div className="lp-content-title">{l.title}</div>
                                <div className="lp-content-dur">{l.durationFmt}</div>
                              </div>
                              {completedSet.has(l.globalIdx) && <span className="lp-content-tick">✓</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Comments tab */}
            {activeTab === "comments" && (
              <div className="lp-tab-body">
                <div className="lp-comments-list">
                  {comments.map(c => (
                    <div className="lp-comment" key={c.id}>
                      <div className="lp-comment-avatar" style={{ background:c.color }}>{c.initial}</div>
                      <div className="lp-comment-content">
                        <div className="lp-comment-header">
                          <span className="lp-comment-name">{c.name}</span>
                          <span className="lp-comment-time">{c.time}</span>
                        </div>
                        <p className="lp-comment-text">{c.text}</p>
                        <div className="lp-comment-actions">
                          <button className={`lp-comment-like${likedIds.has(c.id)?" liked":""}`} onClick={() => toggleLike(c.id)}>
                            <IcoThumb /> {c.likes + (likedIds.has(c.id) ? 1 : 0)}
                          </button>
                          <button className="lp-comment-reply">Reply</button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="lp-comment-form">
                  <textarea
                    className="lp-comment-input"
                    placeholder="Add a comment..."
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    rows={3}
                    onKeyDown={e => { if(e.key==="Enter"&&e.ctrlKey) submitComment(); }}
                  />
                  <button className="lp-comment-submit" onClick={submitComment}>Comment</button>
                </div>
              </div>
            )}

            {/* Notes tab */}
            {activeTab === "notes" && (
              <div className="lp-tab-body">
                <div className="lp-notes-hint">📝 Jot down key takeaways from this lesson</div>
                <div className="lp-note-form">
                  <textarea
                    className="lp-comment-input"
                    placeholder="Write a note..."
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    rows={4}
                  />
                  <button className="lp-comment-submit" onClick={saveNote}>Save Note</button>
                </div>
                {notes.length > 0 ? (
                  <div className="lp-notes-list">
                    {notes.map((n,i) => (
                      <div className="lp-note-item" key={i}>
                        <span className="lp-note-bullet">📌</span><span>{n}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="lp-notes-empty">No notes yet. Start writing above!</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default LessonPlayer;
